import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  ChevronUp,
  ChevronDown
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { EntityEditForm } from '@/components/EntityEditForm'
import { ConfirmationModal } from '@/components/monitoring/ConfirmationModal'
import { useAuth } from '@/hooks/useAuth'

// Types
interface ArchitectFilters {
  search: string
  page: number
  limit: number
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

export function Architects() {
  const [filters, setFilters] = useState<ArchitectFilters>({
    search: '',
    page: 1,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'desc'
  })
  const [selectedArchitects, setSelectedArchitects] = useState<number[]>([])

  const [editingArchitect, setEditingArchitect] = useState<number | null>(null)
  const [creatingArchitect, setCreatingArchitect] = useState(false)
  const [deletingArchitect, setDeletingArchitect] = useState<
    { id: number; name: string } | null
  >(null)

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { canMutate } = useAuth()
  const rowColSpan = canMutate ? 9 : 8

  // Fetch architects with filtering and pagination
  const { data: architectsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['architects', filters],
    queryFn: async () => {
      try {
        console.log('Fetching architects with filters:', filters);
        const params = new URLSearchParams()
        params.append('page', filters.page.toString())
        params.append('limit', filters.limit.toString())
        if (filters.search) params.append('search', filters.search)
        if (filters.sortBy) params.append('sortBy', filters.sortBy)
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)

        const response = await api.get(`/architects?${params}`)
        console.log('Architects response:', response.data);

        return response.data
      } catch (error) {
        console.error('Error fetching architects:', error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: 1000,
  })

  const architects = architectsResponse?.data || []
  const totalArchitects = architectsResponse?.meta?.total || 0
  const totalPages = Math.ceil(totalArchitects / filters.limit)

  // Process architects data
  const processedArchitects = useMemo(() => {
    return architects.map((architect: any) => ({
      ...architect,
      name: architect.name || 'Unnamed Architect',
      nr_projects: Number(architect?.nr_projects ?? 0),
      nr_units: Number(architect?.nr_units ?? 0),
      total_m2: Number(architect?.total_m2 ?? 0),
      nr_units_available: Number(architect?.nr_units_available ?? 0),
      total_m2_available: Number(architect?.total_m2_available ?? 0),
    }))
  }, [architects])

  // Sortable header component
  const SortableHeader = ({ column, children, className = "" }: { column: string; children: React.ReactNode; className?: string }) => {
    const isActive = filters.sortBy === column
    const isAsc = isActive && filters.sortOrder === 'asc'
    const isDesc = isActive && filters.sortOrder === 'desc'

    return (
      <th 
        className={`px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100 select-none ${className}`}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center space-x-1">
          <span>{children}</span>
          <div className="flex flex-col">
            <ChevronUp 
              className={`h-3 w-3 ${isAsc ? 'text-primary-600' : 'text-secondary-400'}`} 
            />
            <ChevronDown 
              className={`h-3 w-3 -mt-1 ${isDesc ? 'text-primary-600' : 'text-secondary-400'}`} 
            />
          </div>
        </div>
      </th>
    )
  }

  // Bulk delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (architectIds: number[]) => {
      const deletePromises = architectIds.map(id => api.delete(`/architects/${id}`))
      await Promise.all(deletePromises)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['architects'] })
      setSelectedArchitects([])
      toast.success('Architects deleted successfully')
    },
    onError: (error) => {
      console.error('Error deleting architects:', error)
      toast.error('Failed to delete architects')
    }
  })

  // Single delete mutation
  const singleDeleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/architects/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['architects'] })
      queryClient.invalidateQueries({ queryKey: ['/architects-dropdown'] })
      toast.success('Architect deleted successfully')
      setDeletingArchitect(null)
    },
    onError: (error: any) => {
      console.error('Error deleting architect:', error)
      const status = error?.response?.status
      const backendMessage = error?.response?.data?.message
      // FK constraint or DB conflict → friendlier message
      if (status === 409 || /foreign key|constraint/i.test(String(backendMessage))) {
        toast.error('Cannot delete: this architect still has associated projects. Reassign them first.')
      } else {
        toast.error(
          Array.isArray(backendMessage) ? backendMessage[0] : backendMessage || 'Failed to delete architect'
        )
      }
    }
  })

  // Handle search
  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm, page: 1 }))
  }

  // Handle sorting
  const handleSort = (column: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc',
      page: 1
    }))
  }

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedArchitects.length === 0) {
      toast.error('Please select architects to delete')
      return
    }

    if (window.confirm(`Are you sure you want to delete ${selectedArchitects.length} architects?`)) {
      deleteMutation.mutate(selectedArchitects)
    }
  }

  // Handle single delete — opens confirmation modal after pre-validating FK usage
  const handleDeleteArchitect = (
    architect: { architect_id: number; name: string; nr_projects: number }
  ) => {
    if (architect.nr_projects > 0) {
      toast.error(
        `Cannot delete "${architect.name}": it has ${architect.nr_projects} associated project${
          architect.nr_projects === 1 ? '' : 's'
        }. Reassign or delete those projects first.`
      )
      return
    }
    setDeletingArchitect({ id: architect.architect_id, name: architect.name })
  }

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedArchitects(processedArchitects.map(architect => architect.architect_id))
    } else {
      setSelectedArchitects([])
    }
  }

  // Handle select individual
  const handleSelectArchitect = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedArchitects(prev => [...prev, id])
    } else {
      setSelectedArchitects(prev => prev.filter(architectId => architectId !== id))
    }
  }

  const handleRowClick = (architectId: number) => {
    navigate(`/architects/${architectId}`)
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-danger-600 mb-2">Error loading architects</div>
          <button
            onClick={() => refetch()}
            className="btn-primary"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 pt-2">
      <div className="bg-white rounded-xl shadow-tagus p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search architects..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
               onClick={() => setCreatingArchitect(true)}
               className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Architect
            </button>
            {selectedArchitects.length > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={deleteMutation.isPending}
                className="btn-danger flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedArchitects.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-tagus overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full divide-y divide-secondary-200 table-fixed">
            <thead className="bg-secondary-50 sticky top-0 z-10">
              <tr>
                <th className="w-16 px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedArchitects.length === processedArchitects.length && processedArchitects.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <SortableHeader column="name" className="text-left">
                  Name
                </SortableHeader>
                <SortableHeader column="nr_projects" className="w-36 text-right">
                  Number Projects
                </SortableHeader>
                <SortableHeader column="nr_units" className="w-32 text-right">
                  Number Units
                </SortableHeader>
                <SortableHeader column="total_m2" className="w-32 text-right">
                  Total M2
                </SortableHeader>
                <SortableHeader column="nr_units_available" className="w-36 text-right">
                  Units Available
                </SortableHeader>
                <SortableHeader column="total_m2_available" className="w-32 text-right">
                  M2 Available
                </SortableHeader>
                <SortableHeader column="created_at" className="w-32 text-left">
                  Created
                </SortableHeader>
                {canMutate && (
                  <th className="w-24 px-6 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {isLoading ? (
                <tr>
                  <td colSpan={rowColSpan} className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="h-5 w-5 animate-spin text-primary-600 mr-2" />
                      Loading architects...
                    </div>
                  </td>
                </tr>
              ) : processedArchitects.length === 0 ? (
                <tr>
                  <td colSpan={rowColSpan} className="px-6 py-4 text-center text-secondary-500">
                    No architects found
                  </td>
                </tr>
              ) : (
                processedArchitects.map((architect) => (
                <tr
                  key={architect.architect_id}
                  className="hover:bg-secondary-50 cursor-pointer"
                  onClick={() => handleRowClick(architect.architect_id)}
                >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedArchitects.includes(architect.architect_id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(e) => handleSelectArchitect(architect.architect_id, e.target.checked)}
                        className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4 truncate max-w-[200px] sm:max-w-xs md:max-w-sm lg:max-w-md xl:max-w-lg">
                      <div className="text-sm font-medium text-secondary-900 truncate" title={architect.name}>
                        {architect.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {architect.nr_projects.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {architect.nr_units.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {architect.total_m2.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {architect.nr_units_available.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {architect.total_m2_available.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {new Date(architect.created_at).toLocaleDateString()}
                    </td>
                    {canMutate && (
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleDeleteArchitect(architect)
                          }}
                          className="inline-flex items-center justify-center p-1.5 rounded-md text-danger-600 hover:bg-danger-50 hover:text-danger-700 focus:outline-none focus:ring-2 focus:ring-danger-500"
                          title={`Delete ${architect.name}`}
                          aria-label={`Delete ${architect.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-secondary-700">
            Showing {((filters.page - 1) * filters.limit) + 1} to {Math.min(filters.page * filters.limit, totalArchitects)} of {totalArchitects} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 1}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-secondary-700">
              Page {filters.page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page === totalPages}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(creatingArchitect || editingArchitect) && (
        <EntityEditForm
          type="architect"
          entityId={editingArchitect}
          onClose={() => {
            setCreatingArchitect(false)
            setEditingArchitect(null)
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['architects'] })
            setCreatingArchitect(false)
            setEditingArchitect(null)
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deletingArchitect !== null}
        onClose={() => setDeletingArchitect(null)}
        onConfirm={() => {
          if (deletingArchitect) singleDeleteMutation.mutate(deletingArchitect.id)
        }}
        title="Delete Architect"
        message={
          deletingArchitect
            ? `Are you sure you want to permanently delete "${deletingArchitect.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={singleDeleteMutation.isPending}
      />
    </div>
  )
}





